import React, { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Loader2, Save, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fieldClass = "w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-ink-50 focus:border-gold outline-none";

export const ValuationBrandSettings = ({ onClose }) => {
    const [settings, setSettings] = useState(null);
    const [preview, setPreview] = useState("");
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const load = async () => {
        const { data } = await api.get("/crm/valuation/brand-settings");
        setSettings(data);
        return data;
    };

    const loadPreview = async (current) => {
        if (!current?.has_logo) { setPreview(""); return; }
        const { data } = await api.get("/crm/valuation/brand-settings/logo", { responseType: "blob" });
        const next = URL.createObjectURL(data);
        setPreview((old) => { if (old) URL.revokeObjectURL(old); return next; });
    };

    useEffect(() => {
        let active = true;
        load().then((data) => { if (active) loadPreview(data); }).catch(() => toast.error("Impostazioni PDF non disponibili"));
        return () => { active = false; };
    }, []);
    useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

    const uploadLogo = async (file) => {
        if (!file) return;
        if (!["image/png", "image/jpeg"].includes(file.type)) { toast.error("Usa un file PNG o JPG"); return; }
        if (file.size > 2 * 1024 * 1024) { toast.error("Il logo non può superare 2 MB"); return; }
        setUploading(true);
        try {
            const body = new FormData();
            body.append("file", file);
            const { data } = await api.post("/crm/valuation/brand-settings/logo", body);
            setSettings({ ...data, agency_name: settings.agency_name, contact: settings.contact, pdf_brand: settings.pdf_brand });
            await loadPreview(data);
            toast.success("Logo caricato");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Caricamento logo non riuscito");
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/crm/valuation/brand-settings", {
                agency_name: settings.agency_name,
                contact: settings.contact,
                pdf_brand: settings.pdf_brand,
            });
            setSettings(data);
            toast.success("Impostazioni PDF salvate");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Salvataggio non riuscito");
        } finally { setSaving(false); }
    };

    if (!settings) return <div data-testid="valuation-brand-loading" className="border border-white/10 p-6 text-ink-400"><Loader2 className="animate-spin" size={18} /></div>;
    const customIncomplete = settings.pdf_brand === "agency" && (!settings.has_logo || !settings.agency_name.trim() || !settings.contact.trim());
    return (
        <section data-testid="valuation-brand-settings" className="border border-white/10 bg-ink-900/35 p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4 mb-5">
                <div><h2 className="font-serif text-xl text-ink-50">Impostazioni PDF</h2><p className="text-xs text-ink-400 mt-1">Il brand selezionato verrà applicato a tutte le valutazioni successive.</p></div>
                <button data-testid="valuation-brand-close" type="button" onClick={onClose} aria-label="Chiudi impostazioni PDF" className="icon-btn"><X size={16} /></button>
            </div>
            <div className="grid md:grid-cols-[180px_1fr] gap-5">
                <div>
                    <div data-testid="valuation-brand-logo-preview" className="aspect-square border border-white/12 bg-white/[.03] grid place-items-center overflow-hidden">
                        {preview ? <img src={preview} alt="Logo agenzia" className="w-full h-full object-contain p-3" /> : <ImageIcon size={30} className="text-ink-500" />}
                    </div>
                    <input ref={inputRef} data-testid="valuation-brand-logo-input" type="file" accept="image/png,image/jpeg" className="hidden" onChange={(event) => uploadLogo(event.target.files?.[0])} />
                    <button data-testid="valuation-brand-logo-upload" type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="cta-outline w-full mt-3 px-3 py-2 text-xs flex items-center justify-center gap-2 disabled:opacity-40">
                        {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {uploading ? "Caricamento..." : "Carica logo"}
                    </button>
                    <p className="text-[10px] text-ink-500 mt-2">PNG o JPG · massimo 2 MB</p>
                </div>
                <div className="space-y-4">
                    <label className="block"><span className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Nome agenzia</span><input data-testid="valuation-brand-agency-name" value={settings.agency_name} onChange={(event) => setSettings({ ...settings, agency_name: event.target.value })} maxLength="160" className={fieldClass} /></label>
                    <label className="block"><span className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Email o contatto footer</span><input data-testid="valuation-brand-contact" value={settings.contact} onChange={(event) => setSettings({ ...settings, contact: event.target.value })} maxLength="240" className={fieldClass} /></label>
                    <label className="block"><span className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Brand nel PDF</span><select data-testid="valuation-brand-mode" value={settings.pdf_brand} onChange={(event) => setSettings({ ...settings, pdf_brand: event.target.value })} className={`${fieldClass} bg-ink-950`}><option value="agency">Brand mia agenzia</option><option value="vendidirect">Brand VendiDirect</option></select></label>
                    {customIncomplete && <p data-testid="valuation-brand-incomplete" className="text-xs text-amber-300">Per attivare il brand agenzia servono logo, nome e contatto.</p>}
                    <button data-testid="valuation-brand-save" type="button" onClick={save} disabled={saving || customIncomplete} className="cta-gold px-5 py-2.5 text-xs flex items-center gap-2 disabled:opacity-40">{saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Salva impostazioni</button>
                </div>
            </div>
        </section>
    );
};